/**
 * SubmissionDetails - I display engagement submission details in a two-column grid layout
 * ONLY concerned with rendering submission position, opportunity, and taxonomy data - no mutations, no state management
 */
import React from 'react';
import type {
  StringifiedResponsiveColumnOptions,
  ResponsiveColumnOptions,
} from '@axiom/ui';
import {
  DateUtil,
  Grid,
  GridColumn,
  GridRow,
  useApi,
  TaxonomyUtil,
} from '@axiom/ui';
import type { CandidateEngagementDetails } from '@axiom/validation';

import { LocationUtil } from '../../utils/location-util';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { EngagementsUtil } from '../../utils/engagements-util';

import { SubmissionDetailsRow } from './SubmissionDetailsRow';

type ColumnWidth = StringifiedResponsiveColumnOptions | ResponsiveColumnOptions;

export type SubmissionDetailsProps = {
  submission: CandidateEngagementDetails;
  name?: string;
  stacked?: boolean;
};

export const SubmissionDetails = ({
  submission,
  name = 'SUBMISSION_DETAILS',
  stacked = false,
}: SubmissionDetailsProps) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const firstColumnEqualWidth: ColumnWidth = stacked ? 12 : 6;
  const secondColumnLabelWidth: ColumnWidth = stacked ? 12 : 4;
  const secondColumnDataWidth: ColumnWidth = stacked ? 12 : 8;

  const engagementLength = EngagementsUtil.engagementLength({
    engagement: submission,
  });

  return (
    <Grid name={name} columns={2} stackable>
      <GridRow>
        <GridColumn>
          <Grid>
            <SubmissionDetailsRow
              label="Estimated Start Date"
              name="ESTIMATED_START_DATE"
              value={
                DateUtil.displayDate(submission.position?.startDate) ?? '--'
              }
              labelWidth={firstColumnEqualWidth}
              dataWidth={firstColumnEqualWidth}
            />
            <SubmissionDetailsRow
              label={
                submission.position?.endDateStatus === 'Confirmed'
                  ? 'End Date'
                  : 'Estimated End Date'
              }
              labelName="END_DATE_LABEL"
              name="ESTIMATED_END_DATE"
              value={DateUtil.displayDate(submission.position?.endDate) ?? '--'}
              labelWidth={firstColumnEqualWidth}
              dataWidth={firstColumnEqualWidth}
            />
            <SubmissionDetailsRow
              label={engagementLength.label}
              name={engagementLength.name}
              value={engagementLength.value}
              labelWidth={firstColumnEqualWidth}
              dataWidth={firstColumnEqualWidth}
            />

            <SubmissionDetailsRow
              label="Weekly Commitment"
              name="WEEKLY_COMMITMENT"
              value={EngagementsUtil.getWeeklyEngagementText({ submission })}
              labelWidth={firstColumnEqualWidth}
              dataWidth={firstColumnEqualWidth}
            />
          </Grid>
        </GridColumn>
        <GridColumn>
          <Grid>
            <SubmissionDetailsRow
              label="Location"
              name="LOCATION"
              value={LocationUtil.displayFormattedJobLocation(
                submission.opportunity,
                submission.position
              )}
              labelWidth={secondColumnLabelWidth}
              dataWidth={secondColumnDataWidth}
            />
            <SubmissionDetailsRow
              label={
                submission.opportunity.primaryPracticeArea &&
                !submission.opportunity.primaryFocusArea
                  ? 'Practice Area'
                  : 'Focus Area'
              }
              labelName="FOCUS_AREA_LABEL"
              name="FOCUS_AREA"
              value={
                TaxonomyUtil.formatPracticeAreaFocusAreaForDisplayWithStandaloneDisplayName(
                  taxonomy,
                  submission.opportunity.primaryPracticeArea,
                  submission.opportunity.primaryFocusArea
                ) ?? '--'
              }
              labelWidth={secondColumnLabelWidth}
              dataWidth={secondColumnDataWidth}
            />
            <SubmissionDetailsRow
              label="Legal Role"
              name="LEGAL_ROLE"
              value={
                submission.position?.businessTitle
                  ? (taxonomy.businessTitles[submission.position.businessTitle]
                      ?.displayName ?? '--')
                  : '--'
              }
              labelWidth={secondColumnLabelWidth}
              dataWidth={secondColumnDataWidth}
            />
            <SubmissionDetailsRow
              label="Client Contact"
              name="CLIENT_CONTACT"
              value={submission.opportunity.clientName ?? '--'}
              labelWidth={secondColumnLabelWidth}
              dataWidth={secondColumnDataWidth}
            />
          </Grid>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
